import { useState, useRef } from 'react'

/**
 * useSpotModeState - Spot mode state management for both map components
 * 
 * Manages state related to the "Spot Something" feature:
 * - Spot mode location (draggable marker)
 * - Selected entity for sighting
 * - Entity type for new entities
 * - Notes and images
 * - Submission state
 * - Input refs for positioning
 * 
 * Used by both HomePageMap and EntityDetailMap.
 * 
 * @returns {Object} Spot mode state and setters
 * 
 * @idempotent - Manages isolated spot mode state
 * @atomic - Single purpose: spot mode functionality
 */
export function useSpotModeState() {
  // Spot location and entity selection
  const [spotModeLocation, setSpotModeLocation] = useState(null) // {lat, lng} for draggable marker
  const [spotModeEntity, setSpotModeEntity] = useState(null) // Selected entity for sighting
  const [spotModeEntityType, setSpotModeEntityType] = useState('food_truck') // Entity type for new entries
  
  // Spot details
  const [spotModeNotes, setSpotModeNotes] = useState('') // User notes for sighting
  const [spotModeEntityStatus, setSpotModeEntityStatus] = useState(null) // 'open' | 'closed' | null
  const [spotModeImages, setSpotModeImages] = useState([]) // Array of image URLs (1-5 required)
  const [spotModeSocials, setSpotModeSocials] = useState({}) // Social links for new entity creation
  
  // Submission state
  const [isSubmittingSpot, setIsSubmittingSpot] = useState(false)
  
  // Analytics timing
  const [spotModeStartTime, setSpotModeStartTime] = useState(null) // Timestamp when spot mode activated
  const [spotModeLocationSetTime, setSpotModeLocationSetTime] = useState(null) // Timestamp when location set
  
  // Refs for entity search input positioning
  const entitySearchInputRef = useRef(null) // Ref for entity search input to position dropdown
  const entitySearchInputRefAlt = useRef(null) // Ref for alternative entity search input (different layout)
  
  // UI state
  const [showShareOptions, setShowShareOptions] = useState(false) // Toggle for share dropdown
  
  return {
    // Location and entity
    spotModeLocation,
    setSpotModeLocation,
    spotModeEntity,
    setSpotModeEntity,
    spotModeEntityType,
    setSpotModeEntityType,
    
    // Spot details
    spotModeNotes,
    setSpotModeNotes,
    spotModeEntityStatus,
    setSpotModeEntityStatus,
    spotModeSocials,
    setSpotModeSocials,
    spotModeImages,
    setSpotModeImages,
    
    // Submission
    isSubmittingSpot,
    setIsSubmittingSpot,
    
    // Analytics timing
    spotModeStartTime,
    setSpotModeStartTime,
    spotModeLocationSetTime,
    setSpotModeLocationSetTime,
    
    // Refs
    entitySearchInputRef,
    entitySearchInputRefAlt,
    
    // UI
    showShareOptions,
    setShowShareOptions
  }
}
